import { ActionModalProps } from '@/components/ActionModal';
import ActionRow from '@/components/ActionRow';
import { SortableItemContainer } from '@/components/DragAndDrop';
import { useActionModal } from '@/stores/useActionModal';
import { Action, RecurringWeekContext } from '@/types/actions';
import rem from '@/utils/rem';
import { validateRecurrence } from '@/utils/events';
import { stripRecurringSeriesShadowRow } from '@/utils/recurringCompletedShadow';
import { FlexProps, ListItem } from '@chakra-ui/react';
import { Data } from '@dnd-kit/core';
import { memo, useCallback } from 'react';

export type ActionListItemProps = {
  disabled?: boolean;
  item: Action;
  index: number;
  dndPayload?: Data;
  readOnly?: boolean;
  showIndex?: boolean;
  actionModalProps?: Partial<ActionModalProps>;
  onDeleteAction?: (action: Action) => void;
  onUpdateBlock?: (blockId: string) => void;
  onClickCreateBlock?: (actionId: string) => void;
  onClickRemoveFromBlock?: (actionId: string) => void;
  withScheduleButton?: boolean;
  progressStatusButtonDisabled?: boolean;
  disableRecurringStatusChanges?: boolean;
  isCompleteMode?: boolean;
  recurringWeekContext?: RecurringWeekContext | null;
} & Omit<FlexProps, 'onClick'>;

function ActionListItem({
  disabled = false,
  item,
  index,
  dndPayload,
  readOnly = false,
  showIndex = true,
  actionModalProps,
  onDeleteAction,
  onUpdateBlock,
  onClickCreateBlock,
  onClickRemoveFromBlock,
  withScheduleButton = false,
  progressStatusButtonDisabled,
  disableRecurringStatusChanges,
  recurringWeekContext = null,
  ...rest
}: ActionListItemProps) {
  const { setActionModalOpen, onGetCurrentActionData: getCurrentActionData } = useActionModal();

  const disableRecurringStatusChangesForRow =
    Boolean(disableRecurringStatusChanges) && validateRecurrence(item?.event?.recurrence);

  const handleToggleEditActionModal = useCallback(
    (action: Action) => {
      getCurrentActionData(stripRecurringSeriesShadowRow(action));
      setActionModalOpen(true, {
        ...actionModalProps,
        isOpenFromRow: true,
      });
    },
    [setActionModalOpen, getCurrentActionData, actionModalProps],
  );

  return (
    <SortableItemContainer id={item.id} disabled={disabled} payload={{ ...dndPayload, category: item.category }}>
      <ListItem marginBottom={rem(6)}>
        <ActionRow
          action={item}
          idx={index + 1}
          onClick={() => handleToggleEditActionModal(item)}
          onUpdateBlock={onUpdateBlock}
          onClickCreateBlock={onClickCreateBlock}
          onClickRemoveFromBlock={onClickRemoveFromBlock}
          readOnly={readOnly}
          showIndex={showIndex}
          withScheduleButton={withScheduleButton}
          progressStatusButtonDisabled={progressStatusButtonDisabled}
          disableRecurringStatusChanges={disableRecurringStatusChangesForRow}
          recurringWeekContext={recurringWeekContext}
          {...rest}
        />
      </ListItem>
    </SortableItemContainer>
  );
}

export default memo(ActionListItem);
